import { useCallback, useMemo } from 'react';
import { FileError } from 'react-dropzone';
import { get, toString } from 'lodash';

import { api } from '@/api';
import { FileImage } from '@/common/constants';
import { FileErrorCode, ServerFileErrorMessage } from '@/components/controls/DropScan/dropScan.types';
import { API_URL } from '@/dtos';
import { DocumentTypesEnum } from '@/modules/MyDocuments/myDocuments.types';
import useCompleteFileUploadQuery from '@/modules/transaction/queries/useCompleteFileUpload.query';
import useCreateFileUploadUrlQuery from '@/modules/transaction/queries/useCreateFileUploadUrl.query';
import useUploadFileToS3Query from '@/modules/transaction/queries/useUploadFileToS3.query';
import { useAuthStore } from '@/modules/user/stores';
import { getFileType } from '@/utils/modifiers/getFileType';

export const getBlob = async (url: string) =>
  fetch(url)
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    .then(async (response: any) => {
      const blob = await response?.arrayBuffer();

      return new Blob([blob], { type: getFileType(url) });
    });

const useFileUploadQuery = () => {
  const { user } = useAuthStore();
  const { createFileUploadUrl } = useCreateFileUploadUrlQuery();
  const { uploadFileToS3 } = useUploadFileToS3Query();
  const { completeFileUpload } = useCompleteFileUploadQuery();

  const createDocumentId = useCallback(
    async (documentType?: DocumentTypesEnum) => {
      try {
        const response = await api.post(API_URL.DOCUMENTS, {
          employee_id: user?.employee?.id,
          document_type: documentType || DocumentTypesEnum.RECEIPT,
        });
        return `${response?.data?.document_id || ''}`;
      } catch {
        return '';
      }
    },
    [user]
  );

  const uploadFile = useCallback(
    async (file: FileImage, documentId: string, ocrRequired = false, onError?: (error?: FileError) => void) => {
      if (!documentId) {
        return file;
      }

      try {
        const uploadUrl = await createFileUploadUrl({
          documentId,
          fileName: file.name,
          employeeId: user?.employee?.id,
        });

        if (!uploadUrl?.data?.upload_url) {
          return;
        }

        const previewResponse = await fetch(file.preview);
        const fileBlob = await previewResponse.blob();
        await uploadFileToS3({ fileBlob, upload_url: uploadUrl?.data?.upload_url });

        const response = await completeFileUpload({
          documentId,
          employeeId: user?.employee?.id,
          fileName: file.name,
          objectKey: uploadUrl?.data?.object_key,
          ocrRequired,
        });

        const responseBlob = await getBlob(response?.data?.s3_url || '');

        return {
          ...file,
          id: toString(response?.data?.id),
          url: response?.data?.s3_url || '',
          blob: responseBlob,
          preview: URL.createObjectURL(responseBlob),
        } as FileImage;
      } catch (error) {
        const errorMessage = get(error, 'response.data.elevate_error_message', '') as string;

        if (errorMessage?.includes(ServerFileErrorMessage.DocumentNameIsTooLong)) {
          onError?.({ code: FileErrorCode.DocumentNameIsTooLong, message: '' });
          return null;
        }

        if (errorMessage?.includes(ServerFileErrorMessage.PasswordProtectedFile)) {
          onError?.({ code: FileErrorCode.PasswordProtectedFile, message: '' });
          return null;
        }

        if (errorMessage?.includes(ServerFileErrorMessage.PasswordProtectedDocument)) {
          onError?.({ code: FileErrorCode.PasswordProtectedDocument, message: '' });
          return null;
        }

        if (onError) {
          onError();
        }
        return null;
      }
    },
    [createFileUploadUrl, uploadFileToS3, completeFileUpload, user?.employee?.id]
  );

  return useMemo(
    () => ({
      createDocumentId,
      uploadFiles: async (
        documentId: string,
        files: FileImage[],
        ocrRequired = false,
        onFileUploadError?: (error?: FileError) => void
      ) => {
        const ids: FileImage[] = [];

        const uploadFileRecursive = async (fileIndex: number = 0) => {
          const uploadedFile = await uploadFile(files[fileIndex], documentId, ocrRequired, onFileUploadError);
          if (uploadedFile) {
            ids.push(uploadedFile);
            if (files[fileIndex + 1]) {
              await uploadFileRecursive(fileIndex + 1);
            }
          }
        };

        await uploadFileRecursive();
        return ids;
      },
    }),
    [createDocumentId, uploadFile]
  );
};

export default useFileUploadQuery;
